import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Users, Target, TrendingUp, Building2, Award, ChevronRight, Clock, CheckSquare } from 'lucide-react';

const PLAYBOOKS = [
    { id: 'outreach', title: 'Daily 10 Outreach', desc: 'Structured daily outreach to generate leads consistently', icon: Users, color: 'bg-blue-100 text-blue-600', steps: 8, duration: '30 min', category: 'Sales' },
    { id: 'referral', title: 'Referral System', desc: 'Build and automate your 5-referral pipeline', icon: Target, color: 'bg-purple-100 text-purple-600', steps: 6, duration: '20 min', category: 'Sales' },
    { id: 'followup', title: 'Follow-Up Sequences', desc: '9-touch email and WhatsApp follow-up cadence', icon: BookOpen, color: 'bg-emerald-100 text-emerald-600', steps: 9, duration: '15 min', category: 'Marketing' },
    { id: 'onboarding', title: 'Client Onboarding', desc: 'Welcome, questionnaire, AI generation, review, deploy', icon: TrendingUp, color: 'bg-amber-100 text-amber-600', steps: 7, duration: '45 min', category: 'Operations' },
    { id: 'commission', title: 'Commission Tracking', desc: 'Track 20% partner commissions and payouts', icon: Building2, color: 'bg-rose-100 text-rose-600', steps: 4, duration: '10 min', category: 'Finance' },
    { id: 'growth', title: 'Goal-to-10 Sprint', desc: 'Get your first 10 customers in 14-30 days', icon: Award, color: 'bg-indigo-100 text-indigo-600', steps: 10, duration: '14 days', category: 'Growth' },
];

const ContentPlaybooks: React.FC = () => {
    const [activePlaybook, setActivePlaybook] = useState<string | null>(null);
    const [filterCat, setFilterCat] = useState<string>('All');

    const categories = ['All', ...new Set(PLAYBOOKS.map(p => p.category))];
    const filtered = filterCat === 'All' ? PLAYBOOKS : PLAYBOOKS.filter(p => p.category === filterCat);

    if (activePlaybook) {
        const pb = PLAYBOOKS.find(p => p.id === activePlaybook);
        if (!pb) return null;
        return (
            <div className="space-y-6">
                <button onClick={() => setActivePlaybook(null)} className="flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors text-xs font-black uppercase tracking-widest">
                    <ChevronRight size={14} className="rotate-180" /> Back to Playbooks
                </button>
                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 space-y-6">
                    <div className="flex items-center gap-4">
                        <div className={`p-4 rounded-2xl ${pb.color}`}><pb.icon size={28} /></div>
                        <div>
                            <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight">{pb.title}</h3>
                            <p className="text-slate-400 text-sm">{pb.desc}</p>
                        </div>
                    </div>
                    <div className="flex gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><CheckSquare size={14} /> {pb.steps} steps</span>
                        <span className="flex items-center gap-1"><Clock size={14} /> {pb.duration}</span>
                    </div>
                    <div className="space-y-3">
                        {Array.from({ length: pb.steps }, (_, i) => (
                            <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer">
                                <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-500">{i + 1}</div>
                                <div className="flex-1">
                                    <p className="font-bold text-slate-700 text-sm">Step {i + 1}: {['Prepare', 'Execute', 'Track', 'Review', 'Optimize', 'Scale', 'Systemize', 'Delegate', 'Automate', 'Grow'][i] || `Action ${i + 1}`}</p>
                                    <p className="text-xs text-slate-400">Detailed instructions for this step</p>
                                </div>
                                <ChevronRight size={16} className="text-slate-300" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex gap-2 flex-wrap">
                {categories.map(cat => (
                    <button key={cat} onClick={() => setFilterCat(cat)}
                        className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${filterCat === cat ? 'bg-blue-600 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        {cat}
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filtered.map(pb => (
                    <motion.button key={pb.id} layout onClick={() => setActivePlaybook(pb.id)}
                        className="group flex items-center gap-4 p-6 rounded-[2rem] bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-blue-900/5 transition-all text-left active:scale-[0.98]">
                        <div className={`p-4 rounded-2xl ${pb.color} transition-transform group-hover:scale-110`}><pb.icon size={24} /></div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-black text-slate-800 uppercase tracking-tight text-sm group-hover:text-blue-700 transition-colors">{pb.title}</h3>
                            <p className="text-slate-400 text-xs font-medium leading-relaxed">{pb.desc}</p>
                            <div className="flex gap-3 mt-2 text-[10px] text-slate-400 font-bold">
                                <span>{pb.steps} steps</span>
                                <span>{pb.duration}</span>
                            </div>
                        </div>
                        <ChevronRight size={18} className="text-slate-300 group-hover:text-blue-500 transition-colors" />
                    </motion.button>
                ))}
            </div>
        </div>
    );
};

export default ContentPlaybooks;
